import { z } from 'zod';

import {
  CONNECTOR_MAX_ACTIONS,
  CONNECTOR_MAX_ACTION_PARAMS,
  CONNECTOR_MAX_FIELDS_PER_GROUP,
  CONNECTOR_MAX_GROUPS,
  CONNECTOR_MAX_LIST_ITEMS,
  CONNECTOR_MAX_SELECT_OPTIONS,
  CONNECTOR_MAX_TIMELINE_ITEMS,
  actionToneSchema,
  connectorDateTimeSchema,
  connectorKeySchema,
  connectorLabelSchema,
  connectorTextSchema,
  connectorUrlSchema,
  externalIdSchema,
  telegramIdSchema,
  toneSchema,
} from './common';

/**
 * GET {project.contextUrl}?telegram_id=…&external_id=…
 *
 * Ключевая идея контракта: проект возвращает не свою предметную модель, а
 * СПЕЦИФИКАЦИЮ ОТРИСОВКИ. Панель контакта рисует groups/actions универсально и
 * ничего не знает ни про балансы, ни про KYC, ни про тарифы. Поэтому добавление
 * поля в карточку на стороне проекта не требует ни строчки кода у нас.
 */

// ─────────────────────────── Поля карточки ───────────────────────────

/** Типы полей. Порядок важен только для читаемости. */
export const CONTEXT_FIELD_TYPES = [
  'text',
  'money',
  'badge',
  'datetime',
  'link',
  'list',
  'progress',
] as const;
export const contextFieldTypeSchema = z.enum(CONTEXT_FIELD_TYPES);
export type ContextFieldType = z.infer<typeof contextFieldTypeSchema>;

const fieldBase = {
  key: connectorKeySchema,
  label: connectorLabelSchema,
  /** Подсказка под значением: единицы, пояснение, дисклеймер. */
  hint: connectorLabelSchema.optional(),
};

/**
 * Код валюты. Не ISO-4217: инвест-проекты оперируют USDT и BTC, а они в три
 * буквы не помещаются. Ограничиваемся алфавитом тикеров.
 */
export const currencyCodeSchema = z
  .string()
  .min(2)
  .max(12)
  .regex(/^[A-Z0-9]+$/, 'currency: только заглавные латинские буквы и цифры');

/** Как показать дату: только дата, только время, полностью или «5 минут назад». */
export const DATETIME_FORMATS = ['date', 'time', 'datetime', 'relative'] as const;
export const datetimeFormatSchema = z.enum(DATETIME_FORMATS);

/**
 * Поле карточки. Discriminated union по type: у money обязательна currency,
 * у badge — tone, у link — url, у list — items, у progress — value и max.
 * Union именно discriminated, а не обычный: ошибка интеграции показывает
 * конкретную ветку и конкретный отсутствующий ключ, а не «ни один вариант
 * не подошёл».
 */
export const contextFieldSchema = z.discriminatedUnion('type', [
  z.object({
    ...fieldBase,
    type: z.literal('text'),
    value: connectorTextSchema,
    /** Показать кнопку «скопировать» — для id, телефонов, адресов кошельков. */
    copyable: z.boolean().default(false),
  }),
  z.object({
    ...fieldBase,
    type: z.literal('money'),
    value: z.number(),
    currency: currencyCodeSchema,
    /** Знаков после запятой; для крипты нужно больше двух. */
    precision: z.number().int().min(0).max(8).default(2),
  }),
  z.object({
    ...fieldBase,
    type: z.literal('badge'),
    value: connectorLabelSchema,
    tone: toneSchema,
  }),
  z.object({
    ...fieldBase,
    type: z.literal('datetime'),
    value: connectorDateTimeSchema,
    format: datetimeFormatSchema.default('datetime'),
  }),
  z.object({
    ...fieldBase,
    type: z.literal('link'),
    url: connectorUrlSchema,
    /** Подпись ссылки. Пусто — показываем сам url. */
    value: connectorLabelSchema.optional(),
  }),
  z.object({
    ...fieldBase,
    type: z.literal('list'),
    items: z.array(connectorTextSchema).max(CONNECTOR_MAX_LIST_ITEMS),
  }),
  z.object({
    ...fieldBase,
    type: z.literal('progress'),
    value: z.number(),
    /** Строго больше нуля: делить на max придётся нам. */
    max: z.number().positive(),
    tone: toneSchema.default('neutral'),
  }),
]);
export type ContextField = z.infer<typeof contextFieldSchema>;

/** Сужение поля по типу: ContextFieldOf<'money'> — ветка с currency. */
export type ContextFieldOf<T extends ContextFieldType> = Extract<ContextField, { type: T }>;

// ─────────────────────────── Группы полей ───────────────────────────

export const contextGroupSchema = z.object({
  title: connectorLabelSchema,
  fields: z.array(contextFieldSchema).max(CONNECTOR_MAX_FIELDS_PER_GROUP),
});
export type ContextGroup = z.infer<typeof contextGroupSchema>;

// ─────────────────────────── Действия ───────────────────────────

export const ACTION_PARAM_TYPES = [
  'text',
  'textarea',
  'number',
  'boolean',
  'date',
  'select',
] as const;
export const actionParamTypeSchema = z.enum(ACTION_PARAM_TYPES);
export type ActionParamType = z.infer<typeof actionParamTypeSchema>;

export const actionParamOptionSchema = z.object({
  value: connectorTextSchema.min(1),
  label: connectorLabelSchema,
});
export type ActionParamOption = z.infer<typeof actionParamOptionSchema>;

const paramBase = {
  key: connectorKeySchema,
  label: connectorLabelSchema,
  required: z.boolean().default(false),
};

/**
 * Описание параметра действия — из него рисуется форма перед вызовом.
 * Тоже discriminated union: select без options — это пустой выпадающий список
 * у оператора, то есть тупик в интерфейсе; ловим на границе.
 */
export const actionParamSpecSchema = z.discriminatedUnion('type', [
  z.object({
    ...paramBase,
    type: z.literal('text'),
    placeholder: connectorLabelSchema.optional(),
    max_length: z.number().int().positive().max(4096).optional(),
  }),
  z.object({
    ...paramBase,
    type: z.literal('textarea'),
    placeholder: connectorLabelSchema.optional(),
    max_length: z.number().int().positive().max(4096).optional(),
  }),
  z.object({
    ...paramBase,
    type: z.literal('number'),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
  }),
  z.object({ ...paramBase, type: z.literal('boolean') }),
  z.object({ ...paramBase, type: z.literal('date') }),
  z.object({
    ...paramBase,
    type: z.literal('select'),
    options: z.array(actionParamOptionSchema).min(1).max(CONNECTOR_MAX_SELECT_OPTIONS),
  }),
]);
export type ActionParamSpec = z.infer<typeof actionParamSpecSchema>;

export const contextActionSchema = z.object({
  key: connectorKeySchema,
  label: connectorLabelSchema,
  tone: actionToneSchema.default('neutral'),
  /**
   * Текст подтверждения. Присутствует — оператор увидит модалку «Одобрить?»
   * до вызова. Деньги в проектах реальные, отменить нажатие нельзя.
   */
  confirm: connectorLabelSchema.optional(),
  params: z.array(actionParamSpecSchema).max(CONNECTOR_MAX_ACTION_PARAMS).default([]),
});
export type ContextAction = z.infer<typeof contextActionSchema>;

// ─────────────────────────── Таймлайн ───────────────────────────

export const contextTimelineItemSchema = z.object({
  at: connectorDateTimeSchema,
  title: connectorLabelSchema,
  tone: toneSchema.default('neutral'),
});
export type ContextTimelineItem = z.infer<typeof contextTimelineItemSchema>;

// ─────────────────────────── Личность ───────────────────────────

export const contextIdentitySchema = z.object({
  external_id: externalIdSchema,
  name: connectorLabelSchema,
  avatar_url: connectorUrlSchema.optional(),
  /** Ссылка в админку проекта — оператор проваливается туда одним кликом. */
  profile_url: connectorUrlSchema.optional(),
});
export type ContextIdentity = z.infer<typeof contextIdentitySchema>;

// ─────────────────────────── Запрос и ответ ───────────────────────────

/**
 * Query GET-запроса контекста. telegram_id есть всегда — он и есть основная
 * стратегия привязки; external_id передаём, если контакт уже связан
 * (Contact.externalId), чтобы проект не искал заново.
 */
export const contextRequestSchema = z.object({
  telegram_id: telegramIdSchema,
  external_id: externalIdSchema.optional(),
});
export type ContextRequest = z.infer<typeof contextRequestSchema>;

/**
 * Ответ. Discriminated union по found: если found=true, identity ОБЯЗАТЕЛЬНА.
 * Иначе панель показывает карточку без имени и без ссылки в проект — состояние,
 * неотличимое от «не нашли», но выглядящее как «нашли».
 *
 * groups/actions/timeline имеют default [] — проект вправе их не присылать,
 * а потребитель всегда получает массив и не пишет проверок на undefined.
 */
export const contextResponseSchema = z.discriminatedUnion('found', [
  z.object({
    found: z.literal(true),
    identity: contextIdentitySchema,
    groups: z.array(contextGroupSchema).max(CONNECTOR_MAX_GROUPS).default([]),
    actions: z.array(contextActionSchema).max(CONNECTOR_MAX_ACTIONS).default([]),
    timeline: z
      .array(contextTimelineItemSchema)
      .max(CONNECTOR_MAX_TIMELINE_ITEMS)
      .default([]),
  }),
  z.object({
    found: z.literal(false),
    /** Почему не нашли — показывается оператору как есть. */
    reason: connectorLabelSchema.optional(),
  }),
]);
export type ContextResponse = z.infer<typeof contextResponseSchema>;
/** То, что проект вправе прислать по проводу (до применения default). */
export type ContextResponseInput = z.input<typeof contextResponseSchema>;

export type ContextResponseFound = Extract<ContextResponse, { found: true }>;

/**
 * Состояние панели у оператора. Отдельный тип, потому что «проект лежит» — не
 * то же самое, что «клиент не найден»: поддержка обязана работать, когда
 * продукт недоступен, и оператор должен видеть разницу.
 */
export const CONTEXT_STATES = ['ok', 'not_found', 'unavailable', 'not_configured'] as const;
export type ContextState = (typeof CONTEXT_STATES)[number];

export type ContextPanel =
  | { state: 'ok'; data: ContextResponseFound; fetchedAt: string; stale: boolean }
  | { state: 'not_found'; reason?: string; fetchedAt: string }
  | { state: 'unavailable'; error: string; staleData?: ContextResponseFound }
  | { state: 'not_configured' };
